import { useAuthStore } from "@/store/auth/auth.store";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import GoogleBtn from "@/components/auth/google.button";
import { useSnackbar } from "notistack";
import { getErrorFromBackend } from "@/utils/errors";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import salesRepSchema from "./sales.rep.schema";
import { acceptInvite, CodeDetails } from "@/services/auth.service";
import { Loader2 } from "lucide-react";
import { setAuthToken, setRefreshToken } from "@/utils/axios-instance";

type SignupFields = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  password2: string;
};

type Props = {
  code: string;
  details?: CodeDetails;
};

const SalesRepForm = ({ code, details }: Props) => {
  const methods = useForm<SignupFields>({
    resolver: zodResolver(salesRepSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      password: "",
      password2: "",
    },
  });
  const { handleSubmit, reset, formState, control } = methods;
  const { isSubmitting } = formState;
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = handleSubmit(async (data) => {
    try {
      const { AccessToken, RefreshToken } = await acceptInvite({
        code,
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
      });
      setAuthToken(AccessToken);
      setRefreshToken(RefreshToken);
      enqueueSnackbar("Account created successfully", { variant: "success" });
      reset();
      useAuthStore.getState().userLoggedIn();
    } catch (e) {
      enqueueSnackbar(getErrorFromBackend(e), { variant: "error" });
    }
  });

  return (
    <Form {...methods}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormItem>
          <FormLabel className="text-gray-800">Email</FormLabel>
          <Input disabled placeholder={details?.email} />
        </FormItem>

        <div className="flex flex-col md:flex-row gap-2 w-full items-start">
          <FormField
            control={control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="text-gray-800">First Name</FormLabel>
                <FormControl>
                  <Input
                    className="bg-white text-black border-gray-400"
                    required
                    placeholder="Enter your First Name"
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="text-gray-800">Last Name</FormLabel>
                <FormControl>
                  <Input
                    className="bg-white text-black border-gray-400"
                    placeholder="Enter your Last Name"
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-800">Password</FormLabel>
              <FormControl>
                <Input
                  className="bg-white text-black border-gray-400"
                  type="password"
                  placeholder="Enter your password"
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="password2"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-800">Confirm Password</FormLabel>
              <FormControl>
                <Input
                  className="bg-white text-black border-gray-400"
                  type="password"
                  placeholder="Confirm your password"
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
        <Button className="w-full bg-sky-500 hover:bg-sky-700 !mt-8" type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="animate-spin" /> : "Create Account"}
        </Button>

        <div className="font-bold my-5 text-center text-gray-800">OR</div>

        <GoogleBtn />
      </form>
    </Form>
  );
};

export default SalesRepForm;
